import { useEffect, useRef, useState, useMemo } from 'react';
import { Button, Flex, View, Checkbox, IconPlayLine, IconPauseLine, IconResetLine, IconMoveStartLine } from '@smarterservices/smarter-ui';
import Video from '../Video';

const InformationalVideo = ({
    isMandatory = false,
    sourceUrl,
    acknowledgementText = 'I acknowledge that I have watched this video.',
    onAcknowledgmentChange = () => {},
    onVideoEnd = () => {},
    buttonLabels = {
        play: 'Play',
        pause: 'Pause',
        replay: 'Replay',
        jumpBack: 'Jump Back',
        acknowledge: 'Acknowledge',
        continue: 'Continue'
    }
}) => {
    const videoRef = useRef(null);
    const [videoCompleted, setVideoCompleted] = useState(false);
    const [videoStarted, setVideoStarted] = useState(false);
    const [acknowledged, setAcknowledged] = useState(false);
    const [videoState, setVideoState] = useState('unplayed');

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.src = sourceUrl;
        }
    }, [sourceUrl, videoRef]);

    const handleVideoEnd = () => {
        setVideoCompleted(true);
        setVideoState('watched');
        onVideoEnd();
    };

    const handleAcknowledge = (e) => {
        setAcknowledged(e.target.checked);
        onAcknowledgmentChange(e.target.checked);
    };


    const handleVideoPlay = () => {
        if (videoRef.current) {
            videoRef.current.play();
            setVideoState('playing');
            setVideoStarted(true);
        }
    };

    const handleVideoPause = () => {
        if (videoRef.current) {
            videoRef.current.pause();
            setVideoState('paused');
        }
    };

    const handleVideoRewind = () => {
        if (videoRef.current) {
            videoRef.current.currentTime = Math.max(
                0,
                videoRef.current.currentTime - 10
            );
            setVideoState('playing');
        }
    };

    const RenderVideoControls = useMemo(() => {
        const playButton = <Button
            color="primary"
            onClick={
                videoCompleted
                    ? () => {
                        videoRef.current.currentTime = 0;
                        handleVideoPlay();
                    }
                    : handleVideoPlay
            }
            margin="0 small"
        >
            <IconPlayLine /> {buttonLabels?.play}
        </Button>;
        const replayButton = <Button
            color="primary"
            onClick={
                videoCompleted
                    ? () => {
                        videoRef.current.currentTime = 0;
                        handleVideoPlay();
                    }
                    : handleVideoPlay
            }
            margin="0 small"
        >
            <IconMoveStartLine /> {buttonLabels?.replay}
        </Button>;
        const pauseButton = <Button
            color="primary"
            onClick={handleVideoPause}
            margin="0 small"
        >
            <IconPauseLine /> {buttonLabels?.pause}
        </Button>;

        const jumpBackButton = <Button
            color="primary"
            onClick={handleVideoRewind}
            margin="0 small"
        >
            <IconResetLine /> {buttonLabels?.jumpBack}
        </Button>;

        if (videoState === 'unplayed') {
            return (
                <Flex direction="row" justifyContent="center" margin="medium 0 0 0">
                    {playButton}
                </Flex>
            );
        } else if (videoState === 'playing') {
            return (
                <Flex direction="row" justifyContent="center" margin="medium 0 0 0">
                    {pauseButton}
                    {jumpBackButton}
                </Flex>
            );
        } else if (videoState === 'paused') {
            return (
                <Flex direction="row" justifyContent="center" margin="medium 0 0 0">
                    {playButton}
                    {jumpBackButton}
                </Flex>
            );
        } else if (videoState === 'watched') {
            return (
                <Flex direction="row" justifyContent="center" margin="medium 0 0 0">
                    {buttonLabels?.replay}
                </Flex>
            );
        }
        return null;
    }, [videoState, videoCompleted, buttonLabels, handleVideoPlay, handleVideoPause, handleVideoRewind]);

    return (
        <Flex direction="column" alignItems="center" width="100%">
            <View as="div">
                <Video
                    streamRef={videoRef}
                    autoPlay={false}
                    mirror={false}
                    muted={false}
                    width="100%"
                    maxHeight="60vh"
                    objectFit="cover"
                    onEnded={handleVideoEnd}
                    controls={false}
                />
            </View>
            {RenderVideoControls}
            <Flex direction="column" alignItems="center" margin="medium 0">
                {((isMandatory && videoCompleted) || !isMandatory) && (
                    <>
                        <Flex
                            direction="row"
                            alignItems="center"
                            margin="0 0 medium 0"
                        >
                            <Checkbox
                                id="acknowledge"
                                label={acknowledgementText}
                                checked={acknowledged}
                                onChange={handleAcknowledge}
                            />
                        </Flex>
                    </>
                )}
            </Flex>
        </Flex>
    );
};

export default InformationalVideo;
